import JSZip from 'jszip';
import * as geofire from 'geofire-common';
import {DisplayField} from '../classes/InfoWindowContent/DisplayField';
import {kmlWithFolders} from '@tmcw/togeojson';
import {gmAPI} from '@/services/MapService';
import {orange} from '../colors.js';

/**
 * @typedef {import('@/services/GraphicService/ImageGraphic/ImageGraphic.js').ImageGraphicData} ImageGraphicData
 * @typedef {import('@/services/GraphicService/PolyGraphic/PolygonGraphic.js').PolygonGraphicData} PolygonGraphicData
 */

// supported image extensions for import from a kmz
const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'svg', 'jfif', 'webp'];

// properties on a KML feature that should not be displayed in the info window
const disallowedProperties = ['fill', 'fill-opacity', 'stroke', 'stroke-opacity', 'stroke-width', 'styleUrl', 'label-opacity', 'label-color', 'label-scale', 'icon-offset', 'icon-offset-units', 'icon', 'name'];

// display fields that should not be editable
const nonEditableFields = ['Acreage Calculated'];

/**
 * unzip a kmz file and return the kml as a string and the images as base64 encoded strings
 * @param {File} kmz
 */
const extractKmz = async (kmz) => {
  const zip = new JSZip();
  const zipFiles = await zip.loadAsync(kmz);
  const files = {
    /** @type {string} */
    kml: null,
    /** @type {Record<string, string>} */
    images: {},
  };
  for (const file in zipFiles.files) {
    if (file.startsWith('__MACOSX') || zipFiles.files[`${file}`].dir) continue; // ignore macosx files and directories
    if (file.endsWith('kml')) {
      files.kml = await zipFiles.file(file).async('string');
    } else if (imageExtensions.some((ext) => file.toLowerCase().endsWith(ext))) {
      files.images[file] = await zipFiles.file(file).async('base64');
    }
  }
  return files;
};

const propertiesToDisplayFields = (properties) => {
  const displayFields = [];
  Object.entries(properties).forEach(([key, value]) => {
    if (key.startsWith('@')) return;
    if (disallowedProperties.includes(key)) return;
    const allowEdit = !nonEditableFields.includes(key);
    displayFields.push(new DisplayField({
      label: key,
      unit: 'string',
      value: value,
      allowEdit: allowEdit,
      allowDelete: allowEdit,
      allowLabelEdit: allowEdit,
    }));
  });
  return displayFields;
};

/**
 * Extracts graphic data from a KML polygon feature.
 * @param {object[]} geometries
 * @param {object} properties
 * @returns
 */
const geometryToGraphicData = (geometries, properties) => {
  const polys = [];
  const llb = new gmAPI.LatLngBounds();
  const isPolyline = geometries[0]?.type === 'Polyline' || geometries[0]?.type === 'LineString';

  geometries.forEach((geometry, index) => {
    if (!geometry.coordinates) {
      console.warn(`geometry type not supported, must have coordinates: `, geometry);
      return;
    }
    const points = [];
    const coordinatesArray = geometry.type === 'LineString' ? geometry.coordinates : geometry.coordinates[0];
    for (const point of coordinatesArray) {
      const constructedPoint = {lat: point[1], lng: point[0]};
      points.push(constructedPoint);
      llb.extend(constructedPoint);
    }
    if (isPolyline) {
      polys.push(points);
    } else {
      polys.push(gmAPI.geometry.encoding.encodePath(points));
    }
  });
  const bounds = llb.toJSON();
  const center = llb.getCenter();
  const geohash = geofire.geohashForLocation([center.lat(), center.lng()]);

  let graphicData;
  if (isPolyline) {
    graphicData = {
      featureType: 'polyline',
      clickable: true,
      path: polys.flatMap((poly) => poly),
      strokeColor: properties.strokeColor ?? orange,
    };
  } else {
    graphicData = {
      featureType: 'polygon',
      clickable: true,
      polys,
      fillColor: properties.fill ?? orange,
    };
  }
  return {graphicData, bounds, geohash};
};

const lineStringToPolyFeature = (lineStringData, isPolyline) => {
  const geometryType = isPolyline ? 'Polyline' : 'Polygon';
  return multiPolyToFeature({
    geometry: {
      geometries: [{
        type: geometryType,
        coordinates: [lineStringData.geometry.coordinates],
      }],
    },
    properties: lineStringData.properties,
  });
};

const polyToFeature = (polyData) => {
  return multiPolyToFeature({
    geometry: {geometries: [polyData.geometry]},
    properties: polyData.properties,
  });
};

const multiPolyToFeature = (multiPolyData) => {
  const {graphicData, bounds, geohash} = geometryToGraphicData(
    multiPolyData.geometry.geometries,
    multiPolyData.properties,
  );

  return {
    bounds,
    geohash,
    graphicData,
    name: multiPolyData.properties.name ?? 'Unknown Polygon',
    displayFields: propertiesToDisplayFields(multiPolyData.properties),
  };
};

/**
 * @param {[number, number]} point1
 * @param {[number, number]} point2
 * @return {number} rotation
 */
const getRotation = (point1, point2) => {
  const rotation = Math.atan2( // atan2(y, x)
    point2[1] - point1[1], // point 2 y - point 1 y
    point2[0] - point1[0], // point 2 x - point 1 x
  ) * 180 / Math.PI; // convert to degrees
  return rotation;// < 0 ? rotation + 360 : rotation;
};

/**
 * Rotate an array of coordinates given a bounding box.
 * Shamelessly stolen from @tmcw/togeojson https://github.com/placemark/togeojson/blob/71b38c6ffaf016b2040225004b3a7ab122d2ed2a/lib/kml/ground_overlay.ts#L40
 * @param {[number, number, number, number]} bbox - The bounding box coordinates [west, south, east, north].
 * @param {[[[number, number],[number, number],[number, number],[number, number]]]} coordinates
 * @param {number} rotation - The rotation angle in degrees.
 */
function rotateBox(bbox, coordinates, rotation) {
  const center = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
  return [
    coordinates[0].map((coordinate) => {
      const dy = coordinate[1] - center[1];
      const dx = coordinate[0] - center[0];
      const distance = Math.sqrt(Math.pow(dy, 2) + Math.pow(dx, 2));
      const angle = Math.atan2(dy, dx) + (rotation * Math.PI) / 180;

      return [
        center[0] + Math.cos(angle) * distance,
        center[1] + Math.sin(angle) * distance,
      ];
    }),
  ];
}

/**
 * the @tmcw/togeojson library pre-rotates the points of the image bounding box. Our ImageOverlayView expects the
 * un-rotated points and the rotation angle. This function extracts the rotation angle from the rotated points and
 * returns the un-rotated points.
 * @param {object} imageData
 * @param {Record<string, string>} files
 */
const imageToFeature = (imageData, files) => {
  const geometry = imageData.geometry;

  // hacky way to get the bounds of the rotated points to get the bounding box to get the center of rotation
  const llb = new gmAPI.LatLngBounds();
  geometry.coordinates[0].forEach((point) => {
    const constructedPoint = {lat: point[1], lng: point[0]};
    llb.extend(constructedPoint);
  });
  const bounds = llb.toJSON();
  const rotation = getRotation(geometry.coordinates[0][0], geometry.coordinates[0][1]);
  const bBox = [bounds.west, bounds.south, bounds.east, bounds.north];
  const coordsCopy = JSON.parse(JSON.stringify(geometry.coordinates));
  // un-rotate the coordinates so that the image is not rotated
  const rotatedCoords = rotateBox(bBox, coordsCopy, -rotation);
  const llb2 = new gmAPI.LatLngBounds();
  rotatedCoords[0].forEach((point) => {
    const constructedPoint = {lat: point[1], lng: point[0]};
    llb2.extend(constructedPoint);
  });
  const rotatedBounds = llb2.toJSON();
  const center = llb2.getCenter();
  const geohash = geofire.geohashForLocation([center.lat(), center.lng()]);
  // any stray whitespace in the filename will break looking up the image
  const filename = imageData.properties.icon.trim();
  /** @type {ImageGraphicData} */
  const graphicData = {
    featureType: 'png',
    bounds: rotatedBounds,
    rotation: rotation,
    image: files[filename],
    imagePath: filename,
  };
  const displayFields = propertiesToDisplayFields(imageData.properties);
  return {
    bounds: rotatedBounds,
    geohash,
    graphicData,
    name: imageData.properties.name ?? 'Unknown Image',
    displayFields,
  };
};

/**
 * @param {Record<string, string>} files
 */
const makeTreeProcessor = (files) => {
  const traverse = (tree) => {
    if (!tree) return;

    /** @type {object[]} */
    const folders = [];
    /** @type {object[]} */
    const features = [];

    for (const node of tree) {
      if (node.type === 'folder' && node.children.length >= 0) {
        const {folders: subFolders, features: subFeatures} = traverse(node.children);
        folders.push({
          children: subFolders,
          folderID: node.meta.description,
          name: node.meta.name,
          showBody: node.meta.open,
          visible: node.meta.visibility,
          defaultVisibility: node.meta.visibility,
        });
        features.push(...subFeatures);
      } else if (node.type === 'Feature') {
        if (node.geometry.type === 'Polygon' && node.properties['@geometry-type'] === 'groundoverlay') {
          features.push(imageToFeature(node, files));
        } else if (node.geometry.type === 'Polygon') {
          features.push(polyToFeature(node));
        } else if (node.geometry.type === 'GeometryCollection') {
          features.push(multiPolyToFeature(node));
        } else if (node.geometry.type === 'LineString') {
          // if the first and last points in node.geometry.coordinates are the same,
          // it's a closed line that can be treated as a polygon
          const firstPoint = node.geometry.coordinates[0];
          const lastPoint = node.geometry.coordinates[node.geometry.coordinates.length - 1];
          const isClosed = firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1];
          features.push(lineStringToPolyFeature(node, !isClosed)); // if the path is not closed, it's a polyline
        } else {
          console.warn(`geometry type not supported: `, node);
          continue;
        }
        folders.push({
          // TODO: make sure a real ID is exported or generated
          id: node.properties.name,
        });
      }
    }

    return {folders, features};
  };

  return traverse;
};

/**
 * Import a KML/KMZ files and get a flat list of features and the folder structure
 * @param {File} inputFile
 */
async function importKMx(inputFile) {
  try {
    const extension = inputFile.name.toLowerCase().split('.').pop();
    if (extension !== 'kmz' && extension !== 'kml') {
      throw new Error('Polygon upload must be a kmz or kml');
    }
    let kmlString;
    let imageData;
    if (extension === 'kmz') {
      const {images, kml} = (await extractKmz(inputFile));
      imageData = images;
      kmlString = kml;
    } else if (extension === 'kml') {
      kmlString = await inputFile.text();
    }
    const geoJson = kmlWithFolders(new DOMParser().parseFromString(kmlString, 'text/xml'));

    if (!(geoJson.type === 'root') || geoJson.children.length === 0) {
      throw new Error('Invalid or empty KML file');
    }

    const processor = makeTreeProcessor(imageData);

    const {features, folders} = processor(geoJson.children);

    return {features, folders};
  } catch (error) {
    error.message = `An error occurred while extracting polygon data from a kmz: ${error.message}`;
    throw error;
  }
}

export {
  importKMx,
};
